import { Parser } from '~/api';
import { Logger, Router } from '~/core';
import { RequestError } from '~/errors';
import { useUserStore } from '~/stores';
import RequestService from './request';
import SettingsService from './settings';

interface LoginFormData {
    method: string;

    email?: string;
    password?: string;

    sso_redirect?: string;
    state?: string;
    code?: string;
}

interface TotpFormData {
    token: string;
}

interface KeyLoginData {
    id: string;
    type: string;
    raw_id: string;
    response: {
        authenticator_data: string;
        client_data_json: string;
        signature: string;
        user_handle?: string;
    };
}

interface ResetPasswordFormData {
    email: string;
}

interface ResetPasswordTokenFormData {
    email: string;
    password: string;
    password_confirmation: string;
}

export interface LoginState {
    finished?: boolean;
    user?: ModelResponse;

    required?: MFAMethods[];
    redirect?: string;
}

class AuthenticationService {
    initializeUser() {
        // TODO: consider data injected from backend
        return RequestService.get('/auth/@me', {
            include: ['notifications', 'announcements'],
        })
            .then((data) => {
                Logger.debug('Authentication', 'User is already authenticated!');

                const userStore = useUserStore();
                userStore.set(Parser.parse(data));
            })
            .catch((err) => {
                if (err instanceof RequestError) {
                    // Ignore if we aren't authenticated.
                    if (err.getStatusCode() === 401) {
                        Logger.debug('Authentication', 'User is not authenticated.');
                        return;
                    }
                }

                throw err;
            })
            .finally(() => {
                const userStore = useUserStore();
                userStore.markInitialized();
            });
    }

    private async loggedIn(data: LoginState) {
        const userStore = useUserStore();
        userStore.set(Parser.parse(data.user!));
        await SettingsService.initializeSettings();

        void import('./notifications').then(({ default: NotificationsService }) => NotificationsService.initializeNotifications());

        return Router.push({
            name: 'index',
        });
    }

    login(data: LoginFormData) {
        return RequestService.post('/auth/login?include[]=notifications&include[]=announcements', data).then(async (data) => {
            if (data.finished) return this.loggedIn(data);

            if (data.redirect) {
                window.location.href = data.redirect;
                return;
            }

            if (data.required?.length) {
                const userStore = useUserStore();
                userStore.setMfa({
                    methods: data.required,
                    webauthn: data.webauthn,
                });

                if (data.required?.includes('webauthn')) {
                    return Router.push({
                        name: 'login.key',
                    });
                } else if (data.required?.includes('totp')) {
                    return Router.push({
                        name: 'login.totp',
                    });
                }
            }
        });
    }

    totp(data: TotpFormData) {
        return RequestService.post('/auth/login/totp?include[]=notifications&include[]=announcements', data).then(async (data) => {
            if (data.finished) return this.loggedIn(data);
        });
    }

    key(data: KeyLoginData) {
        return RequestService.post('/auth/login/key?include[]=notifications&include[]=announcements', data).then(async (data) => {
            if (data.finished) return this.loggedIn(data);
        });
    }

    resetPassword(data: ResetPasswordFormData) {
        return RequestService.post('/auth/forgot/request', data);
    }

    resetPasswordToken(data: ResetPasswordTokenFormData) {
        return RequestService.post('/auth/forgot/reset/:token', data).then(() => {
            return Router.push({
                name: 'login.index',
            });
        });
    }

    logout() {
        return RequestService.post('/auth/logout').then(async () => {
            void import('./notifications').then(({ default: NotificationsService }) => NotificationsService.deinitializeNotifications());

            // TODO: race condition - this should be unset only after redirect finishes (though guest middleware will fail if this is after push :/)
            const userStore = useUserStore();
            userStore.set(undefined);

            await Router.push({
                name: 'login.index',
            });
        });
    }
}

export default new AuthenticationService();
